// ============================================================
// POST-HIT EFFECTS - Condition immunity, death processing,
// and horde magnitude. Pure functions, zero React dependency.
// Extracted from useCombat applyHits.
// ============================================================
import { condType, condDuration } from './conditions';
import { hasWS, getWSP } from './weaponSpecials';
import { rollPlainTargetTest } from './characteristicTests';
import type { Target } from '../types';
import type { HitResult } from './damageCalc';
import { crowdHitsText, isObject, objectTakes } from './bodyShape';
import { UNDYING_DYING_REASON, UNDYING_REASON } from './undyingReason';

/** Conditions to remove when a target dies */
const REMOVE_ON_DEATH = new Set(['Dy', 'U', 'K', 'F', 'S', 'B', 'D']);

export interface ImmunityResult {
  immune: boolean;
  reason: string;
  /** Iron Jaw: Stun may be resisted with a Toughness test (hook handles the roll) */
  ironJawTest?: {
    toughness: number; // Toughness value to test against
  };
}

/** What kind of effect a critical's condition code is, for the immunity rules. */
interface CritCodeKind {
  stun: boolean;
  bleed: boolean;
  fatigue: boolean;
  fire: boolean;
  /** Death or Dying - what Undying refuses. */
  deathly: boolean;
  /** Death itself. */
  dead: boolean;
  /** Blinded, whatever its duration - what the Blind trait refuses. */
  blinding: boolean;
  /** Pinned or Hallucinating - what From Beyond refuses. */
  mindful: boolean;
}

function critCodeKind(condCode: string): CritCodeKind {
  return {
    stun: /^S\d/.test(condCode) || condCode === 'Stunned',
    bleed: condCode === 'BL' || condCode === 'BLS',
    fatigue: /^F\d/.test(condCode),
    fire: condCode === 'Fi',
    deathly: condCode === 'K' || /^Dy\d/.test(condCode) || condCode === 'Dy',
    dead: condCode === 'K',
    blinding: /^B\d*$/.test(condCode),
    mindful: condCode === 'Pn' || /^Hl\d/.test(condCode),
  };
}

/** Undying's words: a death refused (the map plays its fall and rise), or Dying refused (no death to cheat). */
const undyingWords = (k: CritCodeKind) => (k.dead ? UNDYING_REASON : UNDYING_DYING_REASON);

/** One trait's immunity: the reason it gives when it holds, `null` when it does not. */
type ImmunityRule = (target: Target, kind: CritCodeKind, weaponSpecials: string[]) => string | null;

function machineWhat(k: CritCodeKind): string {
  if (k.stun) return 'Stunning';
  return k.bleed ? 'Blood Loss' : 'Fire';
}

/** The immunity rules, in the order they are asked - the first that holds answers. */
const IMMUNITY_RULES: readonly ImmunityRule[] = [
  // From Beyond: immune to Pinning and to the mind deluded (the book; ruling 2026-10-11)
  (t, k) => (t.fromBeyond && k.mindful ? '👁 From Beyond: immune to Pinning and to delusion.' : null),
  // Blind: it has no eyes to lose
  (t, k) => (t.blind && k.blinding ? '👁 Blind: cannot be blinded.' : null),
  // Machine: immune to Stun, Blood Loss and Fire
  (t, k) =>
    (t.machineAP || 0) > 0 && (k.stun || k.bleed || k.fire)
      ? '⚙ Machine: immune to ' + machineWhat(k) + ' from critical damage.'
      : null,
  // Astartes (Larraman's Organ): immune to Blood Loss - EXCEPT vs Warp Weapons
  (t, k, sp) =>
    t.astartes && k.bleed && !hasWS(sp, 'WarpWeapon')
      ? '🩸 Astartes (Larraman Cells): immune to Blood Loss from critical damage.'
      : null,
  // Hardy talent: immune to Blood Loss from critical effects
  (t, k) => (t.hardy && k.bleed ? '💪 Hardy: immune to Blood Loss from critical damage.' : null),
  // Stuff of Nightmares: immune to Bleed, Stun, Fatigue - silent suppression per original code
  (t, k) => (t.stuffOfNightmares && (k.bleed || k.stun || k.fatigue) ? '' : null),
  // Frenzied: immune to Stunning and Fatigue while frenzied
  (t, k) =>
    t.frenzied && (k.stun || k.fatigue)
      ? '⚔ Frenzied: immune to ' + (k.stun ? 'Stunning' : 'Fatigue') + ' while Frenzied.'
      : null,
  // Undying: immune to Death and Dying
  (t, k) => (t.undying && k.deathly ? undyingWords(k) : null),
];

/**
 * Check if a critical effect code should be suppressed based on target traits.
 * Consolidates the 4 duplicated immunity if-chains from applyHits.
 *
 * Order of checks: FromBeyond > Blind > Machine > Astartes > Hardy >
 * StuffOfNightmares > Frenzied > Undying, then Iron Jaw's test.
 *
 * `weaponSpecials` (v6.28.0): specials of the weapon that inflicted the
 * effect - Warp Weapons bypass the Astartes Larraman's-Organ Blood Loss
 * immunity (biological clotting is no defence against warpstuff). Only
 * the Larraman branch checks it; Machine/Hardy/SoN immunities stand.
 */
export function isConditionImmune(condCode: string, target: Target, weaponSpecials: string[] = []): ImmunityResult {
  // An object takes nothing but its destruction (2026-10-26): K still lands, every other code is refused - Purgatus
  // included, since there is no physiology to argue with.
  if (isObject(target) && !objectTakes(condCode)) return { immune: true, reason: 'An object takes no conditions' };
  return livingImmunity(condCode, target, weaponSpecials);
}

/** The immunities of anything that lives: Purgatus, the trait rules, Iron Jaw. */
function livingImmunity(condCode: string, target: Target, weaponSpecials: string[]): ImmunityResult {
  const kind = critCodeKind(condCode);
  // Purgatus (v8.289.0): the weapon argues with physiology and wins -
  // every immunity is bypassed EXCEPT Undying's death immunity, which is
  // existential, not physiological.
  if (hasWS(weaponSpecials, 'Purgatus')) {
    return target.undying && kind.deathly
      ? { immune: true, reason: undyingWords(kind) }
      : { immune: false, reason: '' };
  }
  for (const rule of IMMUNITY_RULES) {
    const reason = rule(target, kind, weaponSpecials);
    if (reason !== null) return { immune: true, reason };
  }
  // Iron Jaw talent: Stun may be resisted with a Toughness test
  if (target.ironJaw && kind.stun && target.ironJawToughness) {
    return {
      immune: false,
      reason: '🤜 Iron Jaw: may resist Stun with Toughness test.',
      ironJawTest: { toughness: target.ironJawToughness },
    };
  }
  return { immune: false, reason: '' };
}

/** Dead targets shed these; a dead target never gains them again. */
const DEAD_SKIP = new Set(['Dy', 'U', 'F', 'S', 'B', 'D']);

/** A penalty with a round count left (`Pen:...:N`) - timed, so death ends it. */
const isTimedPenalty = (ct: string, condCode: string) => ct.startsWith('Pen:') && condDuration(condCode) > 0;

/**
 * Check if a condition should be skipped because the target is already dead.
 * Dead targets don't accumulate Dying, Unconscious, Fatigue, Stun, Blind,
 * Deaf, or timed Penalty conditions.
 */
export function isDeadSkip(condCode: string): boolean {
  const ct = condType(condCode);
  return DEAD_SKIP.has(ct) || isTimedPenalty(ct, condCode);
}

/**
 * Apply death: filter conditions through REMOVE_ON_DEATH set, add 'K'.
 * Returns new conditions array (does not mutate input).
 */
export function applyDeath(conditions: string[]): string[] {
  const cleaned = conditions.filter((c) => {
    const ct = condType(c);
    return !REMOVE_ON_DEATH.has(ct) && !isTimedPenalty(ct, c);
  });
  cleaned.push('K');
  return cleaned;
}

export interface HordeDamageResult {
  magDmg: number;
  magAfter: number;
  narrativeNotes: string[];
}

/** The magnitude a damaging attack takes, term by term, for the sum and the summary. */
interface MagnitudeTerms {
  dmgHits: number;
  tidebreaker: boolean;
  blast: number;
  devastating: number;
  panic: number;
}

/**
 * Panicking(X) (v8.288.0): the damaged crowd tests Willpower or X more
 * magnitude routs. Rolled after every other die of the attack, so the
 * seeded sequence stays aligned. Returns the magnitude that routed.
 */
function panicRout(specialRules: string[], target: Target | undefined, notes: string[]): number {
  const panicking = getWSP(specialRules, 'Panicking');
  if (!(panicking > 0 && target)) return 0;
  const test = rollPlainTargetTest(target, 'willpower', 0, 0);
  if (test.success) {
    notes.push('😱 Panicking: WP ' + test.roll + ' vs ' + test.target + ' - the horde holds.');
    return 0;
  }
  notes.push('😱 Panicking: WP ' + test.roll + ' vs ' + test.target + ' FAILED - ' + panicking + ' more rout!');
  return panicking;
}

/** Blast's magnitude: the larger of the area block's radius (v8.372.3) and the legacy chip. */
const blastOf = (specialRules: string[], blastRadius: number) =>
  Math.max(Math.floor(blastRadius), getWSP(specialRules, 'Blast'));

/**
 * Official Devastating(X): +X Magnitude on a damaging hit (v8.197.13 -
 * the melee damage rider it replaced lived in damageCalc).
 */
const devastatingOf = (specialRules: string[]) =>
  hasWS(specialRules, 'Devastating') ? getWSP(specialRules, 'Devastating') : 0;

const positive = (n: number) => (n > 0 ? n : 0);

function hordeSummary(t: MagnitudeTerms, dos: number, magDmg: number, before: number, after: number): string {
  if (magDmg <= 0) return '\uD83D\uDCCA Horde: No hits dealt damage - 0 Magnitude damage.';
  return [
    '📊 Horde: ' + t.dmgHits + ' damaging hit(s)',
    t.tidebreaker ? ' (Tidebreaker: hits count double)' : '',
    ' + ' + dos + ' DoS',
    t.blast > 0 ? ' + Blast(' + t.blast + ')' : '',
    t.devastating > 0 ? ' + Devastating(' + t.devastating + ')' : '',
    t.panic > 0 ? ' + Panicking(' + t.panic + ')' : '',
    ' = ' + magDmg + ' Magnitude damage. (' + before + ' → ' + after + ')',
  ].join('');
}

/** What the attack left of the horde: destroyed, or the extra hits it still scores (+1 per 10 magnitude, ruling 2026-10-09). */
function hordeAftermath(magDmg: number, magAfter: number): string | null {
  if (magDmg <= 0) return null;
  if (magAfter <= 0) return '💀 HORDE DESTROYED! Magnitude reduced to 0!';
  return '🗡 Horde attacks with ' + crowdHitsText(magAfter) + ' (' + magAfter + ' Magnitude ÷ 10).';
}

/**
 * The hits that dealt damage, as the horde counts them. Tidebreaker
 * (v8.288.0): each damaging hit counts DOUBLE against the crowd - the
 * multiplicative sibling of Devastating's flat bonus.
 */
function damagingHits(hits: HitResult[], specialRules: string[]) {
  const raw = hits.filter((h) => !h.ffBlocked && h.effectiveDamage > 0).length;
  const tidebreaker = hasWS(specialRules, 'Tidebreaker');
  return { dmgHits: tidebreaker ? raw * 2 : raw, tidebreaker };
}

/**
 * Calculate horde magnitude damage from attack hits.
 * Magnitude damage = damaging hits + DoS + Blast value (if damage dealt).
 */
export function processHordeDamage(
  hits: HitResult[],
  dos: number,
  specialRules: string[],
  currentMagnitude: number,
  maxMagnitude: number,
  /** Panicking(X)'s Willpower test needs the horde itself (v8.288.0). */
  target?: Target,
  /** The blast's radius in metres (v8.372.3) - the rulebook's +Blast(X) magnitude, from the area block. */
  blastRadius = 0,
): HordeDamageResult {
  const notes: string[] = [];
  const { dmgHits, tidebreaker } = damagingHits(hits, specialRules);
  const landed = dmgHits > 0;
  const terms: MagnitudeTerms = {
    dmgHits,
    tidebreaker,
    blast: blastOf(specialRules, blastRadius),
    devastating: devastatingOf(specialRules),
    panic: landed ? panicRout(specialRules, target, notes) : 0,
  };
  // +DoS, +Blast and +Devastating only if at least one hit got through.
  const magDmg = landed ? dmgHits + dos + positive(terms.blast) + positive(terms.devastating) + terms.panic : 0;
  const magAfter = Math.max(0, currentMagnitude - magDmg);
  notes.push(hordeSummary(terms, dos, magDmg, currentMagnitude, magAfter));
  const aftermath = hordeAftermath(magDmg, magAfter);
  if (aftermath) notes.push(aftermath);
  return { magDmg, magAfter, narrativeNotes: notes };
}
